// **********************************************************************
//
// WebAudioTrack
//
// A WebAudio Track with an interface that matches a HTML Audio Element
//
// **********************************************************************

import EventEmitter from '@/helpers/EventEmitter'
import { context, masterGain } from 'src/helpers/WebAudio'

const Events = {
  LOAD: 'load',
  END: 'end',
  PLAY: 'play',
  PAUSE: 'pause',
  STATE: 'state'
}

class WebAudioTrack {
  constructor(trackConfig) {
    Object.assign(this, EventEmitter)

    // CONFIG
    this.id = trackConfig.id
    this.config = JSON.parse(JSON.stringify(trackConfig))

    this.state = '';
    this.Events = Events

    // LOAD STATE
    this.loading = {
      progress: 0,
      ready: false,
      completed: false
    }

    // OBJECTS
    this.gainNode = context.createGain()
    this.gainNode.connect(masterGain)
    this.gainNode.gain.value = 0

    this.buffer = null
    this.source = null

    this.playing = false
    this.isEnded = false
    this.isMuted = false
    this.currVolume = 0
    this.currTime = 0

    this.time = 0
  }

  load = () => {
    return new Promise((resolve, reject) => {
      const onLoad = () => {
        context.decodeAudioData(xhr.response, buffer => {
          this.buffer = buffer
          this._setState(Events.LOAD)
          resolve()
        })
      }

      const onProgress = evt => {
        let progress = 0
        if (evt && evt.lengthComputable) {
          progress = evt.loaded / evt.total
        }
        this.loading.progress = progress
      }

      let xhr = new XMLHttpRequest()

      xhr.onprogress = onProgress
      xhr.onload = onLoad
      xhr.onerror = reject
      xhr.onabort = reject

      xhr.open('GET', this.config.src[0].url)
      xhr.responseType = 'arraybuffer'
      xhr.send()
    })
  }

  _setState = (type) => {
    this.state = type
    this.emit(type)
    this.emit(Events.STATE, type)
  }

  _onEnded = () => {
    this.playing = false
    this.isEnded = true
    this.currTime = this.buffer.duration
    this._setState(Events.END)
  }

  play = () => {
    this.source = context.createBufferSource()
    this.source.buffer = this.buffer
    this.source.connect(this.gainNode)
    this.source.start(0, this.time)
    this.startTime = window.performance.now()
    this.playing = true
    this.source.onended = this._onEnded
    this._setState(Events.PLAY)
  }

  pause = () => {
    if (!this.playing) return
    if (!this.source) return
    this.source.onended = null
    this.source.stop()
    this.source.disconnect()
    this.currTime = (window.performance.now() - this.startTime) / 1000
    this.time += this.currTime
    this.playing = false
    this._setState(Events.PAUSE)
  }

  get volume() {
    return this.currVolume
  }

  set volume(val) {
    this.currVolume = val
    if (!this.isMuted) {
      this.gainNode.gain.value = val
    }
  }

  get muted() {
    return this.isMuted
  }

  set muted(val) {
    this.isMuted = val
    this.gainNode.gain.value = this.isMuted ? 0 : this.currVolume
  }

  get currentTime() {
    if (this.playing) {
      return (window.performance.now() - this.startTime) / 1000
    } else {
      return this.currTime
    }
  }

  set currentTime(val) {
    if (!this.source) return

    this.source.onended = null
    try {
      this.source.stop()
      this.source.disconnect()
    } catch (err) {
      console.log(err)
    }
    this.currTime = val
    this.time = val

    if (val > this.buffer.duration) {
      this._onEnded()
    } else {
      this.isEnded = false
      if (!this.paused) {
        this.play()
      }
    }
  }

  get duration() {
    return this.buffer.duration
  }

  get paused() {
    return !this.playing
  }

  get ended() {
    return this.isEnded
  }
}

export default WebAudioTrack
